import React from 'react';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { appColors } from '../assets/appColors';

type GraphProps = Readonly<{
  title: string;
  percentage: string | null;
  isFirstItem?: boolean;
}>;

const parsePercentage = (percentage: string): number => {
  const cleaned = percentage.replace(/[^0-9]/g, '');
  const value = parseInt(cleaned, 10);

  if (percentage.includes('<') && value < 5) {
    return 5; // Assume '<5%' to be 5% for the graph
  }

  return value;
};

export const Graph: React.FC<GraphProps> = ({ title, percentage, isFirstItem }) => {
  if (!percentage) {
    return null;
  }

  const parsedPercentage = parsePercentage(percentage);

  return (
    <Box sx={{ ...graphContainer, mt: { xs: isFirstItem ? 1 : 2, md: 0 } }}>
      <Typography sx={titleStyle}>{title}</Typography>
      <Box sx={barBackgroundStyle}>
        <Box sx={barLevelStyle(parsedPercentage)}>
          <Typography fontWeight={700}>{parsedPercentage}%</Typography>
        </Box>
      </Box>
    </Box>
  );
};

const graphContainer: SxProps<Theme> = {
  display: {
    xs: 'flex',
    md: 'grid',
  },
  flexDirection: 'column',
  gridTemplateColumns: '1fr 2fr',
  mt: { xs: 2, md: 0 },
} as const;

const titleStyle: SxProps<Theme> = {
  fontSize: '16px',
  marginBottom: '8px',
  fontWeight: 500,
} as const;

const barBackgroundStyle: SxProps<Theme> = {
  height: '24px',
  backgroundColor: appColors.color.taupe2,
  borderRadius: '5px',
  position: 'relative',
} as const;

const barLevelStyle = (percentage: number): SxProps<Theme> =>
  ({
    height: '100%',
    width: `${percentage}%`,
    backgroundColor: appColors.neutral.lightGray,
    borderRadius: '2px',
    position: 'relative',
    fontWeight: 800,
    fontSize: '12px',
    color: appColors.neutral.black,
    paddingLeft: '10px',
  }) as const;
